import {
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, from, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import { TeamsAuthService } from '../services/teams/teams-auth.service';

/**
 * Teams-mode replacement for MsalInterceptor.
 *
 * When running inside Teams there is no MSAL session, so this interceptor
 * attaches a Bearer token obtained via the Teams SSO -> OBO exchange to
 * requests hitting the core API origin. The subscription-key interceptor still
 * runs separately to add Ocp-Apim-Subscription-Key.
 *
 * Registered only in Teams mode (see createAppConfig in app.config.ts).
 */
export const teamsAuthInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const serverBaseUrl = environment.apiConfig.serverBaseUrl;
  const apiOrigin = new URL(serverBaseUrl).origin;

  const targetsApi = req.url.startsWith(apiOrigin);
  // The OBO exchange itself authenticates with the raw Teams SSO token via
  // fetch, so never try to attach an OBO token to that call (would recurse).
  const isOboCall = req.url.includes('/auth/teams-obo');

  if (!targetsApi || isOboCall) {
    return next(req);
  }

  const teamsAuth = inject(TeamsAuthService);
  return from(teamsAuth.getApiToken()).pipe(
    switchMap((token) =>
      next(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }))
    )
  );
};
